import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    Button,
    ButtonGroup,
    Checkbox,
    Dialog,
    DialogContent,
    FormControl,
    Grid,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Joi from 'joi';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useAxios from '../../../../hooks/useAxios';
import useInventory from '../../../../hooks/useInventory';
import {
    arraySextforceAutoRepostActionPreviousPost,
    arraySextforceAutoRepostActionPreviousPostDisplay,
    schemaSextforceAutoRepost,
    SextforceAutoRepost,
    SextforceAutoRepostActionPreviousPost,
    SextforceAutoRepostWithLatestRepostLog,
} from '../../../../hooks/useSextforceAutoRepost';
import useSextforceAutoRepostColors from '../../../../hooks/useSextforceAutoRepostColors';
import useSextforceCaptions from '../../../../hooks/useSextforceCaptions';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import useSubscriberPosts from '../../../../hooks/useSubscriberPosts';
import { SettingsContext } from '../../../../store/SettingsContext';
import { UserContext } from '../../../../store/UserContext';
import { handleHttpError } from '../../../../utils/common';
import InventoryTemplateTagsSelector from '../../../common/InventoryTemplateTagsSelector';
import InventoryTemplateVariantsSelector from '../../../common/InventoryTemplateVariantsSelector';
import TemplateModeToggle, { TemplateSubMode } from '../../../common/TemplateModeToggle';
import OnlyFansAlbumSelector from '../../../common/OnlyFansAlbumSelector';
import OnlyFansPostPreview from '../../../common/OnlyFansPostPreview';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

const momentPeriods: { [key: string]: string } = {
    minutes: 'Minutes',
    hours: 'Hours',
    days: 'Days',
    weeks: 'Weeks',
    months: 'Months',
};

type Props = {
    subscriber: OnlyFansSubscriber;
    autoRepost: SextforceAutoRepostWithLatestRepostLog;
    onClose: () => void;
    open: boolean;
    refetchAutoReposts: () => void;
};

const SextforceAutoRepostEditRepostDialog = (props: Props) => {
    const { subscriber, autoRepost, onClose, open, refetchAutoReposts } = props;
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const dialog = useDialog();
    const axios = useAxios();
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();

    const { data: post, isLoading: isPostLoading } = useSubscriberPosts(
        autoRepost.type === 'post' && autoRepost.latestRepostLog ? autoRepost.latestRepostLog.postId : autoRepost.postId,
    );
    const [isPostValid, setIsPostValid] = useState<boolean>(false);

    useEffect(() => {
        if (post && !isPostLoading && post.canEdit) {
            setIsPostValid(true);
        } else {
            setIsPostValid(false);
        }
    }, [post, isPostLoading]);

    const { data: sextforceAutoRepostColors, isLoading: isSextforceAutoRepostColorsLoading } = useSextforceAutoRepostColors();
    const { data: contentInventory, isLoading: isContentInventoryLoading } = useInventory();
    const { categories: captionCategories = [] } = useSextforceCaptions(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [formData, setFormData] = useState<SextforceAutoRepost>({
        subscriberId: subscriber._id!,
        name: autoRepost.name,
        description: autoRepost.description,
        colorId: autoRepost.colorId,
        active: autoRepost.active,
        originalPostUrl: autoRepost.originalPostUrl,
        type: autoRepost.type,
        repostEveryAmount: autoRepost.repostEveryAmount,
        repostEveryUnit: autoRepost.repostEveryUnit,
        repostWhenCampaignHitsTarget: autoRepost.repostWhenCampaignHitsTarget,
        pinPost: autoRepost.pinPost || false,
        repostCount: autoRepost.repostCount,
        privateArchiveAfterActive: autoRepost.privateArchiveAfterActive || false,
        privateArchiveAfterAmount: autoRepost.privateArchiveAfterAmount || 1,
        privateArchiveAfterUnit: autoRepost.privateArchiveAfterUnit || 'hours',
        actionPreviousPost: autoRepost.actionPreviousPost,
        nextRepostDate: autoRepost.nextRepostDate,
        startDate: autoRepost.startDate,
        endDate: autoRepost.endDate,
        sourceAlbumId: autoRepost.sourceAlbumId,
        postCaption: autoRepost.postCaption ?? false,
        captionCategory: autoRepost.captionCategory || '',
        postImage: autoRepost.postImage ?? false,
        postVideo: autoRepost.postVideo ?? false,
        postAudio: autoRepost.postAudio ?? false,
        postGif: autoRepost.postGif ?? false,
        templateIds: autoRepost.templateIds || [],
        templateTags: autoRepost.templateTags || [],
        templateTagsPaidOnly: autoRepost.templateTagsPaidOnly ?? true,
    });
    const [templateSubMode, setTemplateSubMode] = useState<TemplateSubMode>(
        (autoRepost.templateTags?.length ?? 0) > 0 ? 'tags' : 'ids',
    );

    // Validate the form data
    const errors: Joi.ValidationErrorItem[] | null = schemaSextforceAutoRepost.validate(formData).error?.details || null;
    const hasAtLeastOneAlbumMedia = !!(formData.postImage || formData.postVideo || formData.postAudio || formData.postGif);
    const selectedTemplateIds = formData.templateIds || [];
    const selectedTemplateTags = formData.templateTags || [];
    const isPaidPage = Number(subscriber?.ofSubscriptionPrice || 0) !== 0;

    useEffect(() => {
        if (!open) {
            setIsSaving(false);
        } else {
            setFormData({
                subscriberId: subscriber._id!,
                name: autoRepost.name,
                description: autoRepost.description,
                colorId: autoRepost.colorId,
                active: autoRepost.active,
                originalPostUrl: autoRepost.originalPostUrl,
                type: autoRepost.type,
                repostEveryAmount: autoRepost.repostEveryAmount,
                repostEveryUnit: autoRepost.repostEveryUnit,
                repostWhenCampaignHitsTarget: autoRepost.repostWhenCampaignHitsTarget,
                pinPost: autoRepost.pinPost || false,
                repostCount: autoRepost.repostCount,
                privateArchiveAfterActive: autoRepost.privateArchiveAfterActive || false,
                privateArchiveAfterAmount: autoRepost.privateArchiveAfterAmount || 1,
                privateArchiveAfterUnit: autoRepost.privateArchiveAfterUnit || 'hours',
                actionPreviousPost: autoRepost.actionPreviousPost,
                nextRepostDate: autoRepost.nextRepostDate,
                startDate: autoRepost.startDate,
                endDate: autoRepost.endDate,
                sourceAlbumId: autoRepost.sourceAlbumId,
                postCaption: autoRepost.postCaption ?? false,
                captionCategory: autoRepost.captionCategory || '',
                postImage: autoRepost.postImage ?? false,
                postVideo: autoRepost.postVideo ?? false,
                postAudio: autoRepost.postAudio ?? false,
                postGif: autoRepost.postGif ?? false,
                templateIds: autoRepost.templateIds || [],
                templateTags: autoRepost.templateTags || [],
                templateTagsPaidOnly: autoRepost.templateTagsPaidOnly ?? true,
            });
            setTemplateSubMode((autoRepost.templateTags?.length ?? 0) > 0 ? 'tags' : 'ids');
        }
    }, [
        autoRepost.actionPreviousPost,
        autoRepost.active,
        autoRepost.description,
        autoRepost.colorId,
        autoRepost.endDate,
        autoRepost.name,
        autoRepost.nextRepostDate,
        autoRepost.originalPostUrl,
        autoRepost.pinPost,
        autoRepost.repostCount,
        autoRepost.repostEveryAmount,
        autoRepost.repostEveryUnit,
        autoRepost.repostWhenCampaignHitsTarget,
        autoRepost.privateArchiveAfterActive,
        autoRepost.privateArchiveAfterAmount,
        autoRepost.privateArchiveAfterUnit,
        autoRepost.startDate,
        autoRepost.type,
        autoRepost.sourceAlbumId,
        autoRepost.postCaption,
        autoRepost.postImage,
        autoRepost.postVideo,
        autoRepost.postAudio,
        autoRepost.postGif,
        autoRepost.templateIds,
        autoRepost.templateTags,
        autoRepost.templateTagsPaidOnly,
        open,
        subscriber._id,
    ]);

    const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason && reason === 'backdropClick') {
            return;
        }

        onClose();
    };

    const handleSave = () => {
        if (formData.type === 'album' && !hasAtLeastOneAlbumMedia) {
            enqueueSnackbar('Select at least one media type for album repost.', { variant: 'warning' });
            return;
        }

        if (formData.type === 'template' && templateSubMode === 'ids' && selectedTemplateIds.length === 0) {
            enqueueSnackbar('Select at least one template.', { variant: 'warning' });
            return;
        }

        if (formData.type === 'template' && templateSubMode === 'tags' && selectedTemplateTags.length === 0) {
            enqueueSnackbar('Select at least one template tag.', { variant: 'warning' });
            return;
        }

        const updateAutoRepost = async () => {
            if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber && autoRepost) {
                const query: string = `${settingsContext.routes.autoRepost.base}${params.userId}/${autoRepost._id}`;

                setIsSaving(true);

                // Update campaign settings
                const updatedAutoRepost = await axios
                    .put(query, formData)
                    .then(async response => response.data)
                    .catch(error => {
                        console.error(error);
                        setIsSaving(false);
                        handleHttpError(error, dialog);
                    });

                if (updatedAutoRepost && updatedAutoRepost._id) {
                    enqueueSnackbar('Auto Repost updated successfully.', { variant: 'success' });

                    onClose();
                    refetchAutoReposts();
                } else {
                    enqueueSnackbar('Auto Repost could not be updated.', { variant: 'error' });
                }

                setIsSaving(false);
            }
        };

        updateAutoRepost();
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} disableEscapeKeyDown>
                <StyledCardTitleBar title="Edit Auto Repost" />
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Grid container alignItems={'center'} justifyContent={'center'} spacing={1.5}>
                            <Grid item xs={12}>
                                {isPostLoading && <LinearProgress color="primary" />}
                                {post && !post.canEdit && (
                                    <Typography variant="body2" color={'error'} marginTop={0.5}>
                                        This post is not valid or you do not have permission to repost it.
                                    </Typography>
                                )}
                                {post && !isPostLoading && isPostValid && formData.postId !== 0 && <OnlyFansPostPreview post={post} />}
                            </Grid>
                            <Grid item xs={12}>
                                Name
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                    Name of this promo or the person you are promoting.{' '}
                                    <em>This will be shown in the Telegram notification to your colleagues.</em>
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    id="name"
                                    name="name"
                                    size="small"
                                    fullWidth
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    error={!!errors?.find(error => error.context?.key === 'name')}
                                    helperText={errors ? errors.find(error => error.context?.key === 'name')?.message : undefined}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                Description
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                    Any description or notes you would like to leave for this promo. These will not appear in the Telegram
                                    notification to your colleagues.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    id="description"
                                    name="description"
                                    size="small"
                                    fullWidth
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    error={!!errors?.find(error => error.context?.key === 'description')}
                                    helperText={errors ? errors.find(error => error.context?.key === 'description')?.message : undefined}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                Category Color
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                    Choose an identifying color for this repost. This will be used to help identify the category or topic of
                                    the repost.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Select
                                    id="color"
                                    size="small"
                                    fullWidth
                                    value={formData.colorId || 'none'}
                                    onChange={e =>
                                        setFormData({ ...formData, colorId: e.target.value === 'none' ? undefined : e.target.value })
                                    }
                                >
                                    <MenuItem value="none">
                                        <Stack direction="row" spacing={1} alignItems={'center'} justifyContent={'flex-start'}>
                                            <CircleOutlinedIcon
                                                color="disabled"
                                                sx={{
                                                    diaplay: 'inline-block',
                                                    fontSize: 20,
                                                }}
                                            />
                                            <Typography variant="body1" color={'text.primary'}>
                                                None
                                            </Typography>
                                        </Stack>
                                    </MenuItem>
                                    {!isSextforceAutoRepostColorsLoading &&
                                        sextforceAutoRepostColors &&
                                        sextforceAutoRepostColors.map(color => (
                                            <MenuItem key={color._id} value={color._id}>
                                                <Stack direction="row" spacing={1} alignItems={'center'} justifyContent={'flex-start'}>
                                                    <CircleIcon
                                                        sx={{
                                                            diaplay: 'inline-block',
                                                            color: color.color,
                                                            fontSize: 20,
                                                        }}
                                                    />
                                                    <Typography variant="body1" color={'text.primary'}>
                                                        {color.name}
                                                    </Typography>
                                                </Stack>
                                            </MenuItem>
                                        ))}
                                </Select>
                            </Grid>
                            {formData.type === 'album' && (
                                <>
                                    <Grid item xs={12}>
                                        Source Album
                                        <Typography variant="body2" color={'text.secondary'} marginTop={0.5} marginBottom={0.5}>
                                            Select an album to post content from. The system will create a new post with content from this
                                            album.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <OnlyFansAlbumSelector
                                            size="small"
                                            vaultAlbum={formData.sourceAlbumId ?? 0}
                                            handleAlbumChange={e => {
                                                const v = e.target.value;
                                                setFormData({
                                                    ...formData,
                                                    sourceAlbumId: v === '' ? undefined : Number.parseInt(v, 10),
                                                });
                                            }}
                                            disabled={false}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        Content to Include
                                        <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                            Which types of content from the album should be posted?
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Stack direction="column" flexWrap="wrap" gap={0} useFlexGap>
                                            <FormControl component="fieldset" sx={{ mt: 0, mb: 0 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Checkbox
                                                        size="medium"
                                                        checked={!!formData.postImage}
                                                        onChange={e => setFormData({ ...formData, postImage: e.target.checked })}
                                                    />
                                                    <Typography variant="body2">Images</Typography>
                                                </Stack>
                                            </FormControl>
                                            <FormControl component="fieldset" sx={{ mt: 0, mb: 0 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Checkbox
                                                        size="medium"
                                                        checked={!!formData.postVideo}
                                                        onChange={e => setFormData({ ...formData, postVideo: e.target.checked })}
                                                    />
                                                    <Typography variant="body2">Videos</Typography>
                                                </Stack>
                                            </FormControl>
                                            <FormControl component="fieldset" sx={{ mt: 0, mb: 0 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Checkbox
                                                        size="medium"
                                                        checked={!!formData.postAudio}
                                                        onChange={e => setFormData({ ...formData, postAudio: e.target.checked })}
                                                    />
                                                    <Typography variant="body2">Audio</Typography>
                                                </Stack>
                                            </FormControl>
                                            <FormControl component="fieldset" sx={{ mt: 0, mb: 0 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Checkbox
                                                        size="medium"
                                                        checked={!!formData.postGif}
                                                        onChange={e => setFormData({ ...formData, postGif: e.target.checked })}
                                                    />
                                                    <Typography variant="body2">GIFs</Typography>
                                                </Stack>
                                            </FormControl>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl component="fieldset" sx={{ mt: 0, width: '100%' }}>
                                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                                <Checkbox
                                                    size="medium"
                                                    checked={!!formData.postCaption}
                                                    onChange={e => setFormData({ ...formData, postCaption: e.target.checked })}
                                                />
                                                <Typography variant="body2" noWrap sx={{ flexShrink: 0 }}>
                                                    Include a random caption
                                                </Typography>
                                                {formData.postCaption && (
                                                    <Select
                                                        fullWidth
                                                        size="small"
                                                        displayEmpty
                                                        value={formData.captionCategory || ''}
                                                        onChange={e => setFormData({ ...formData, captionCategory: e.target.value })}
                                                        // show placeholder on empty
                                                        renderValue={selected => {
                                                            if (selected === '') {
                                                                return <Typography variant="body2">All Caption Categories</Typography>;
                                                            }

                                                            const category = captionCategories.find(c => c.name === selected);
                                                            return (
                                                                <Typography variant="body2">
                                                                    {category ? category.name : selected}
                                                                </Typography>
                                                            );
                                                        }}
                                                    >
                                                        <MenuItem value="">All captions</MenuItem>
                                                        {captionCategories.map(category => (
                                                            <MenuItem key={category._id} value={category.name}>
                                                                {category.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            </Stack>
                                        </FormControl>
                                    </Grid>
                                </>
                            )}
                            {formData.type === 'caption' && (
                                <>
                                    <Grid item xs={12}>
                                        Caption Category
                                        <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                            A random caption will be selected from your Sextforce captions and posted as-is as a text-only
                                            post. Choose a category to pick from, or leave it on All captions.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Select
                                            fullWidth
                                            size="small"
                                            displayEmpty
                                            value={formData.captionCategory || ''}
                                            onChange={e => setFormData({ ...formData, captionCategory: e.target.value })}
                                            renderValue={selected => {
                                                if (selected === '') {
                                                    return <Typography variant="body2">All Caption Categories</Typography>;
                                                }

                                                const category = captionCategories.find(c => c.name === selected);
                                                return <Typography variant="body2">{category ? category.name : selected}</Typography>;
                                            }}
                                        >
                                            <MenuItem value="">All captions</MenuItem>
                                            {captionCategories.map(category => (
                                                <MenuItem key={category._id} value={category.name}>
                                                    {category.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>
                                </>
                            )}
                            {formData.type === 'template' && (
                                <>
                                    <Grid item xs={12}>
                                        Templates
                                        <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                            Select one or more templates from content inventory, or select one or more template tags to
                                            draw from every template carrying any of those tags.
                                        </Typography>
                                    </Grid>
                                    {isPaidPage && (
                                        <Grid item xs={12}>
                                            <Alert severity="warning" icon={false}>
                                                Paid posts are not allowed on a paid page! Auto Reposts that post paid PPVs will get
                                                deactivated.
                                            </Alert>
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <TemplateModeToggle
                                            value={templateSubMode}
                                            onChange={nextMode => {
                                                setTemplateSubMode(nextMode);
                                                setFormData({ ...formData, templateIds: [], templateTags: [] });
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        {templateSubMode === 'ids' ? (
                                            <>
                                                <InventoryTemplateVariantsSelector
                                                    inventory={contentInventory}
                                                    loading={isContentInventoryLoading}
                                                    selectedTemplates={selectedTemplateIds}
                                                    onChange={nextTemplates => setFormData({ ...formData, templateIds: nextTemplates })}
                                                />
                                                {!!errors?.find(error => error.context?.key === 'templateIds') && (
                                                    <Typography variant="caption" color="error">
                                                        {errors.find(error => error.context?.key === 'templateIds')?.message}
                                                    </Typography>
                                                )}
                                            </>
                                        ) : (
                                            <InventoryTemplateTagsSelector
                                                inventory={contentInventory}
                                                loading={isContentInventoryLoading}
                                                value={selectedTemplateTags}
                                                onChange={nextTags => setFormData({ ...formData, templateTags: nextTags })}
                                                paidOnly={formData.templateTagsPaidOnly ?? true}
                                                onPaidOnlyChange={paidOnly =>
                                                    setFormData({ ...formData, templateTagsPaidOnly: paidOnly })
                                                }
                                            />
                                        )}
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12}>
                                Repost Every
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                    How often should the post be reposted? A small random amount of minutes will be added automatically so
                                    to not look like a bot.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Alert severity="warning" icon={false}>
                                    Note: There is no point in reposting posts too frequently because nobody will get a chance to see them -
                                    especially if you have many auto reposts setup at the same time. They will just be fighting each other
                                    for attention. Make sure to look at the statistics of each repost here to see how many users viewed the
                                    post.
                                </Alert>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container alignItems={'center'} justifyContent={'center'} spacing={1}>
                                    <Grid item xs>
                                        <TextField
                                            id="repostEveryAmount"
                                            name="repostEveryAmount"
                                            fullWidth
                                            size="small"
                                            value={Number.isNaN(formData.repostEveryAmount) ? '' : formData.repostEveryAmount}
                                            onChange={e =>
                                                setFormData({
                                                    ...formData,
                                                    repostEveryAmount: Number.isNaN(Number.parseInt(e.target.value, 10))
                                                        ? 0
                                                        : Number.parseInt(e.target.value, 10),
                                                })
                                            }
                                            error={!!errors?.find(error => error.context?.key === 'repostEveryAmount')}
                                            helperText={
                                                errors
                                                    ? errors.find(error => error.context?.key === 'repostEveryAmount')?.message
                                                    : undefined
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs="auto">
                                        <Select
                                            value={formData.repostEveryUnit}
                                            onChange={e => {
                                                setFormData({
                                                    ...formData,
                                                    repostEveryUnit: e.target.value as moment.DurationInputArg2,
                                                });
                                            }}
                                            fullWidth
                                            size="small"
                                            sx={{ mt: '4px' }}
                                        >
                                            {Object.keys(momentPeriods).map(item => (
                                                <MenuItem value={item} key={item}>
                                                    {momentPeriods[item]}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                Repost When Tip Campaign Hits Target
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                    Should the post be reposted when the tip campaign hits its target and not wait for the repost time?
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <ButtonGroup fullWidth disabled={!autoRepost.isPostTipCampaign || autoRepost.type !== 'post'}>
                                    <Button
                                        variant={formData.repostWhenCampaignHitsTarget ? 'contained' : 'outlined'}
                                        color="primary"
                                        size="large"
                                        onClick={() => setFormData({ ...formData, repostWhenCampaignHitsTarget: true })}
                                    >
                                        Yes
                                    </Button>
                                    <Button
                                        variant={!formData.repostWhenCampaignHitsTarget ? 'contained' : 'outlined'}
                                        color="primary"
                                        size="large"
                                        onClick={() => setFormData({ ...formData, repostWhenCampaignHitsTarget: false })}
                                    >
                                        No
                                    </Button>
                                </ButtonGroup>
                            </Grid>
                            <Grid item xs={12}>
                                Until Date (optional)
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                    If you want the repost to stop at a specific date, enter it here. If you don't enter a date, the repost
                                    will continue indefinitely.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container alignItems={'center'} justifyContent={'center'} spacing={0.5}>
                                    <Grid item xs="auto">
                                        <Checkbox
                                            size="medium"
                                            checked={typeof formData.endDate !== 'undefined'}
                                            onChange={e => {
                                                if (e.target.checked) {
                                                    setFormData({ ...formData, endDate: new Date() });
                                                } else {
                                                    setFormData({ ...formData, endDate: undefined });
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <MobileDateTimePicker
                                            disabled={!formData.endDate}
                                            value={formData.endDate}
                                            onChange={date =>
                                                setFormData({ ...formData, endDate: date ? moment(date).toDate() : undefined })
                                            }
                                            renderInput={props => <TextField fullWidth size="small" {...props} />}
                                            ampm={true}
                                            ampmInClock={true}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            {formData.type === 'link' && (
                                <Grid item xs={12}>
                                    <FormControl component="fieldset" sx={{ mt: 0, width: '100%' }}>
                                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                            <Checkbox
                                                size="medium"
                                                checked={!!formData.postCaption}
                                                onChange={e => setFormData({ ...formData, postCaption: e.target.checked })}
                                            />
                                            <Typography variant="body2" noWrap sx={{ flexShrink: 0 }}>
                                                Include a random caption
                                            </Typography>
                                            {formData.postCaption && (
                                                <Select
                                                    fullWidth
                                                    size="small"
                                                    displayEmpty
                                                    value={formData.captionCategory || ''}
                                                    onChange={e => setFormData({ ...formData, captionCategory: e.target.value })}
                                                    // show placeholder on empty
                                                    renderValue={selected => {
                                                        if (selected === '') {
                                                            return <Typography variant="body2">All Caption Categories</Typography>;
                                                        }

                                                        const category = captionCategories.find(c => c.name === selected);
                                                        return (
                                                            <Typography variant="body2">{category ? category.name : selected}</Typography>
                                                        );
                                                    }}
                                                >
                                                    <MenuItem value="">All captions</MenuItem>
                                                    {captionCategories.map(category => (
                                                        <MenuItem key={category._id} value={category.name}>
                                                            {category.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        </Stack>
                                    </FormControl>
                                    <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                        A random caption will be selected from your Sextforce captions and the link will be added on a new
                                        line below it.
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                Pin Post
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                    Should the new post be pinned to the top of your feed?
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <ButtonGroup fullWidth>
                                    <Button
                                        variant={formData.pinPost ? 'contained' : 'outlined'}
                                        color="primary"
                                        size="large"
                                        onClick={() => setFormData({ ...formData, pinPost: true })}
                                    >
                                        Yes
                                    </Button>
                                    <Button
                                        variant={!formData.pinPost ? 'contained' : 'outlined'}
                                        color="primary"
                                        size="large"
                                        onClick={() => setFormData({ ...formData, pinPost: false })}
                                    >
                                        No
                                    </Button>
                                </ButtonGroup>
                            </Grid>
                            <Grid item xs={12}>
                                Hide/Expire Post
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                    The system will private archive the post after a the time period you set here to hide the post from your
                                    feed, but allow the system to repost it again next time. It's similar to the "Expire Post" feature on
                                    OnlyFans, but you can set your own time period.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container alignItems={'center'} justifyContent={'center'} spacing={1}>
                                    <Grid item xs="auto">
                                        <Checkbox
                                            size="medium"
                                            checked={!!formData.privateArchiveAfterActive}
                                            onChange={e =>
                                                setFormData({
                                                    ...formData,
                                                    privateArchiveAfterActive: e.target.checked,
                                                    ...(!e.target.checked
                                                        ? {
                                                              privateArchiveAfterAmount: 1,
                                                              privateArchiveAfterUnit: 'hours',
                                                          }
                                                        : {}),
                                                    ...(e.target.checked &&
                                                    formData.type === 'post' &&
                                                    formData.actionPreviousPost === 'privateArchive'
                                                        ? { actionPreviousPost: 'none' }
                                                        : {}),
                                                })
                                            }
                                            sx={{ mt: '4px' }}
                                        />
                                    </Grid>
                                    <Grid item xs={true}>
                                        <TextField
                                            disabled={!formData.privateArchiveAfterActive}
                                            id="privateArchiveAfterAmount"
                                            name="privateArchiveAfterAmount"
                                            fullWidth
                                            size="small"
                                            value={
                                                Number.isNaN(formData.privateArchiveAfterAmount) ? '' : formData.privateArchiveAfterAmount
                                            }
                                            onChange={e =>
                                                setFormData({
                                                    ...formData,
                                                    privateArchiveAfterAmount: Number.isNaN(Number.parseInt(e.target.value, 10))
                                                        ? 0
                                                        : Number.parseInt(e.target.value, 10),
                                                })
                                            }
                                            error={!!errors?.find(error => error.context?.key === 'privateArchiveAfterAmount')}
                                            helperText={
                                                errors
                                                    ? errors.find(error => error.context?.key === 'privateArchiveAfterAmount')?.message
                                                    : undefined
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs="auto">
                                        <Select
                                            disabled={!formData.privateArchiveAfterActive}
                                            value={formData.privateArchiveAfterUnit}
                                            onChange={e => {
                                                setFormData({
                                                    ...formData,
                                                    privateArchiveAfterUnit: e.target.value as moment.DurationInputArg2,
                                                });
                                            }}
                                            fullWidth
                                            size="small"
                                            sx={{ mt: '4px' }}
                                        >
                                            {Object.keys(momentPeriods).map(item => (
                                                <MenuItem value={item} key={item}>
                                                    {momentPeriods[item]}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                What To Do With The Previous Post?
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                    What should be done with the previous post when the new one is posted? If you choose to delete or
                                    archive the post then the post will be deleted or archived after the new post is posted.
                                    <em>
                                        Note: if you've chosen to repost a link, the original post will not be touched. Only the following
                                        reposts will be affected by this setting.
                                    </em>
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Select
                                    id="actionPreviousPost"
                                    fullWidth
                                    size="small"
                                    value={formData.actionPreviousPost}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            actionPreviousPost: e.target.value as SextforceAutoRepostActionPreviousPost,
                                        })
                                    }
                                >
                                    {arraySextforceAutoRepostActionPreviousPost.map(action => (
                                        <MenuItem
                                            key={action}
                                            value={action}
                                            disabled={
                                                formData.privateArchiveAfterActive &&
                                                action === 'privateArchive' &&
                                                formData.type === 'post'
                                                    ? true
                                                    : false
                                            }
                                            sx={{
                                                color:
                                                    action === 'delete' && formData.type === 'post' ? theme.palette.error.main : 'inherit',
                                            }}
                                        >
                                            {`${
                                                arraySextforceAutoRepostActionPreviousPostDisplay[
                                                    arraySextforceAutoRepostActionPreviousPost.indexOf(action)
                                                ]
                                            }${action === 'delete' && formData.type === 'post' ? ' (Are you sure?)' : ''}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            {/* {formData.actionPreviousPost === 'delete' && (
                                <Grid item xs={12}>
                                    <Alert severity="warning" icon={false}>
                                        OnlyFans has a limit of how many posts can be deleted every day. The limit is an inconvenient 20% of
                                        the total number of posts. Keep that in mind when you set the frequency of your reposts.
                                    </Alert>
                                </Grid>
                            )} */}
                        </Grid>
                    </LocalizationProvider>
                </DialogContent>
                <StyledDialogActions>
                    <Button
                        color="error"
                        size="medium"
                        disabled={isSaving}
                        onClick={() => {
                            handleClose({}, 'escapeKeyDown');
                        }}
                    >
                        Close
                    </Button>
                    <LoadingButton
                        color="secondary"
                        variant="contained"
                        size="medium"
                        loading={isSaving}
                        disabled={
                            !!errors ||
                            (formData.type === 'album' &&
                                (formData.sourceAlbumId == null || formData.sourceAlbumId < 1 || !hasAtLeastOneAlbumMedia)) ||
                            (formData.type === 'template' &&
                                (templateSubMode === 'ids' ? selectedTemplateIds.length === 0 : selectedTemplateTags.length === 0))
                        }
                        onClick={() => {
                            handleSave();
                        }}
                    >
                        <span>Save Settings</span>
                    </LoadingButton>
                </StyledDialogActions>
            </Dialog>
        </>
    );
};

export default SextforceAutoRepostEditRepostDialog;
